import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Empty,
  Popconfirm,
  Segmented,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import type {
  AttendanceStatus,
  AttendanceTodaySection,
  RegisterEntryRow,
} from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Attendance today. Real per-section status: number of
 * students, submission state, and counts once entries exist. Clicking
 * "Open register" opens a Drawer with the roster and Present / Absent
 * / Late / Sick / Leave segmented buttons per student. Submit turns
 * the register state green.
 */
export function AttendanceTodayPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const today = new Date().toISOString().slice(0, 10);
  const [openRegister, setOpenRegister] = useState<AttendanceTodaySection | null>(null);

  const q = useQuery({
    queryKey: ['attendance-today', institution, today],
    queryFn: () => productApi.getAttendanceToday(institution, today),
    enabled: !!institution,
  });

  const dayLabel = new Date(today).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  const totalSections = q.data?.sections.length ?? 0;
  const notStarted = q.data?.sections.filter((s) => s.state === 'not_started').length ?? 0;
  const submitted = q.data?.sections.filter((s) => s.state === 'submitted').length ?? 0;
  const open = q.data?.sections.filter((s) => s.state === 'open').length ?? 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Attendance · {dayLabel}</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Registers show <b>submission state</b>. A missing register is actionable, a percentage isn't.
        </Typography.Text>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Sections" value={totalSections} /></Card>
        <Card size="small"><Statistic title="Not started" value={notStarted} valueStyle={{ color: notStarted > 0 ? '#a35800' : undefined }} /></Card>
        <Card size="small"><Statistic title="In progress" value={open} /></Card>
        <Card size="small"><Statistic title="Submitted" value={submitted} valueStyle={{ color: submitted > 0 ? '#1c6442' : undefined }} /></Card>
      </div>

      <Card size="small" title={`Registers (${totalSections})`}>
        {q.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
        {q.data && q.data.sections.length === 0 && (
          <Empty description="No enrolled students to mark yet." style={{ padding: 20 }} />
        )}
        {q.data && q.data.sections.length > 0 && (
          <Table<AttendanceTodaySection>
            size="small"
            pagination={false}
            rowKey={(s) => `${s.grade}-${s.section}`}
            dataSource={q.data.sections}
            columns={[
              { title: 'Grade · section', key: 'g', render: (_, s) => <b>{s.grade}{s.section ? ` · ${s.section}` : ''}</b> },
              { title: 'Students', dataIndex: 'total_students', key: 't', width: 100 },
              {
                title: 'State',
                key: 'state',
                width: 120,
                render: (_, s) => STATE_TAG[s.state],
              },
              { title: 'Present', dataIndex: 'present', key: 'p', width: 80 },
              { title: 'Absent', dataIndex: 'absent', key: 'a', width: 80, render: (v: number) => v > 0 ? <b style={{ color: '#b3382f' }}>{v}</b> : v },
              { title: 'Late', dataIndex: 'late', key: 'l', width: 60 },
              { title: 'Sick', dataIndex: 'sick', key: 's', width: 60 },
              { title: 'Unmarked', dataIndex: 'unmarked', key: 'u', width: 90 },
              {
                key: 'action',
                width: 130,
                align: 'right',
                render: (_, s) => (
                  <Button size="small" type={s.state === 'not_started' ? 'primary' : 'default'} onClick={() => setOpenRegister(s)}>
                    Open register
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Card>

      <RegisterDrawer
        section={openRegister}
        date={today}
        institution={institution}
        onClose={() => setOpenRegister(null)}
      />
    </Space>
  );
}

const STATE_TAG: Record<'not_started' | 'open' | 'submitted', React.ReactNode> = {
  not_started: <Tag color="warning">not started</Tag>,
  open: <Tag color="processing">open</Tag>,
  submitted: <Tag color="success">submitted</Tag>,
};

function RegisterDrawer({
  section, date, institution, onClose,
}: { section: AttendanceTodaySection | null; date: string; institution: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<Record<string, AttendanceStatus>>({});

  const reg = useQuery({
    queryKey: ['register', institution, date, section?.grade, section?.section],
    queryFn: () => productApi.getRegister(institution, date, section!.grade, section!.section),
    enabled: !!section,
  });

  const mark = useMutation({
    mutationFn: (submit: boolean) => productApi.markAttendance(institution, {
      date,
      grade: section!.grade,
      section: section!.section,
      entries: Object.entries(pending).map(([student_id, status]) => ({ student_id, status })),
      submit,
    }),
    onSuccess: (_v, submit) => {
      setPending({});
      void qc.invalidateQueries({ queryKey: ['register', institution, date, section?.grade, section?.section] });
      void qc.invalidateQueries({ queryKey: ['attendance-today', institution] });
      void qc.invalidateQueries({ queryKey: ['briefing', institution] });
      void message.success(submit ? 'Register submitted' : 'Saved');
    },
  });

  const entries: RegisterEntryRow[] = reg.data?.entries ?? [];
  const totalPending = Object.keys(pending).length;

  return (
    <Drawer
      open={!!section}
      onClose={() => { setPending({}); onClose(); }}
      title={section ? `${section.grade}${section.section ? `-${section.section}` : ''} · ${date}` : ''}
      width={720}
      destroyOnClose
      extra={
        <Space>
          <Button
            disabled={totalPending === 0 || mark.isPending}
            onClick={() => mark.mutate(false)}
            loading={mark.isPending && !mark.variables}
          >
            Save {totalPending > 0 && `(${totalPending})`}
          </Button>
          <Popconfirm
            title="Submit register?"
            description="Marks the register as submitted for the day. You can still re-mark; the audit log tracks changes."
            onConfirm={() => mark.mutate(true)}
            okText="Submit"
          >
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              disabled={mark.isPending}
              loading={mark.isPending && mark.variables === true}
            >
              Submit
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      {reg.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
      {reg.data && entries.length === 0 && <Empty description="No enrolled students in this section." />}
      {entries.length > 0 && (
        <>
          {reg.data?.state === 'submitted' && (
            <Alert style={{ marginBottom: 12 }} type="success" showIcon message="Register submitted. Re-marking still allowed; each change is audited." />
          )}
          <Table<RegisterEntryRow>
            size="small"
            pagination={false}
            rowKey="student_id"
            dataSource={entries}
            columns={[
              { title: 'Adm. no.', dataIndex: 'admission_number', key: 'a', width: 110, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Name', dataIndex: 'name', key: 'n' },
              {
                title: 'Status',
                key: 's',
                render: (_, r) => {
                  const chosen = pending[r.student_id] ?? r.status ?? undefined;
                  return (
                    <Segmented
                      size="small"
                      value={chosen}
                      onChange={(v) => setPending((p) => ({ ...p, [r.student_id]: v as AttendanceStatus }))}
                      options={[
                        { label: 'P', value: 'present' },
                        { label: 'A', value: 'absent' },
                        { label: 'L', value: 'late' },
                        { label: 'Sk', value: 'sick' },
                        { label: 'Lv', value: 'leave' },
                      ]}
                    />
                  );
                },
              },
            ]}
          />
        </>
      )}
    </Drawer>
  );
}
